// suzani-burst — sn01 "Sunnat To'yi" preview card. A terracotta +
// cobalt suzani foil frame on cream paper wraps an inner card; the
// boy's portrait sits inside a circular embroidered hoop, names
// appear in Yeseva One, and the caption pairs the date with the
// venue. Suzani embroidery, mahalla joy — no Western carnival.
import * as O from "@/components/Ornaments";
import { Photo, type LayoutCtx } from "./_shared";

// Cobalt deep — the second suzani thread color.
const COBALT = "#1F3A8A";

export function SuzaniBurstLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const captionDate = formatDate(date);
  // Suzani foil: terracotta + saffron + cobalt — two-thread embroidery.
  const SUZANI_FOIL = `linear-gradient(135deg, ${accent} 0%, #E8B07A 22%, #F4E3C5 45%, ${COBALT} 65%, ${accent} 100%)`;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: SUZANI_FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 3px ${bg}, inset 0 0 0 4px ${COBALT}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: COBALT,
            marginBottom: "5%",
          }}
        >
          SUNNAT TO&apos;YI
        </div>

        {/* Embroidered hoop with portrait. */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          {/* Outer suzani ring */}
          <svg
            viewBox="0 0 100 100"
            aria-hidden
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          >
            <circle cx="50" cy="50" r="49" fill="none" stroke={accent} strokeWidth="0.6" />
            <circle cx="50" cy="50" r="46" fill="none" stroke={COBALT} strokeWidth="0.4" opacity="0.65" />
            {/* Crewel-stitch ticks. */}
            {Array.from({ length: 16 }).map((_, i) => {
              const a = (i * 22.5 * Math.PI) / 180;
              return (
                <line
                  key={i}
                  x1={50 + Math.cos(a) * 46.5}
                  y1={50 + Math.sin(a) * 46.5}
                  x2={50 + Math.cos(a) * 49}
                  y2={50 + Math.sin(a) * 49}
                  stroke={i % 2 === 0 ? accent : COBALT}
                  strokeWidth="0.7"
                />
              );
            })}
            {/* Cardinal dotted rosettes. */}
            {[0, 90, 180, 270].map((deg) => {
              const a = (deg * Math.PI) / 180;
              return (
                <g key={deg} transform={`translate(${50 + Math.cos(a) * 50}, ${50 + Math.sin(a) * 50})`}>
                  <circle r="2" fill={accent} />
                  <circle r="0.9" fill={bg} />
                </g>
              );
            })}
          </svg>

          {/* Portrait circle */}
          <div
            style={{
              position: "absolute",
              inset: "8%",
              borderRadius: "50%",
              overflow: "hidden",
              border: `2px solid ${accent}`,
              boxShadow: `0 0 0 2px ${bg}, 0 0 0 3px ${COBALT}55`,
              background: `${accent}10`,
            }}
          >
            <Photo
              src={photo}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              fallback={
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "grid",
                    placeItems: "center",
                    background: `radial-gradient(circle at 50% 45%, ${accent}33, ${bg} 70%)`,
                    color: accent,
                  }}
                >
                  <O.Suzani size={48} color={accent} />
                </div>
              }
            />
          </div>
        </div>

        {/* Names — Yeseva One */}
        <div
          style={{
            fontFamily: font,
            fontSize: 28,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
            letterSpacing: "0.01em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          SUNNAT TO&apos;YI · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${parseInt(m[1], 10)} ${months[parseInt(m[2], 10) - 1] ?? ""} ${m[3]}`;
}
